import React, { useCallback, useState, useEffect, ReactNode, useContext } from 'react';
import history from 'services/history';
import { api } from 'services/api';
import { setUser } from 'store/modules/user/actions';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';

interface AuthContextData {
  login(username: string, password: string): Promise<boolean>;
  logout(): Promise<boolean>;
  checkAuth(): boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  loggingOff: boolean;
}

const AuthContext = React.createContext({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [loggingOff, setLoggingOff] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    setIsLoading(false);

    const token = localStorage.getItem('universo-plastica@token');

    if (!token) {
      return;
    }

    setIsLoading(true);

    api
      .post(`/users/check-token`, { token })
      .then(response => {
        dispatch(setUser(response.data.user));
        setIsAuthenticated(response.data.valid);
      })
      .catch(err => {
        if (err.response) console.error(err.response.data.error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  const login = useCallback(
    async (login: string, password: string): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        api
          .post('/users/login', { login, password })
          .then(_response => {
            const response = _response.data;
            localStorage.setItem('universo-plastica@token', response.token);
            dispatch(setUser({ ...response.user[0], user_profile: response.user.user_profile }));
            setIsAuthenticated(true);
            resolve(true);
          })
          .catch(err => {
            if (err.response) {
              if (err.response.status === 401) reject(err);
            } else reject(err);
          });
      });
    },
    [dispatch],
  );

  const logout = useCallback((): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      setLoggingOff(true);
      api
        .post('/users/logout')
        .then(() => {
          localStorage.removeItem('universo-plastica@token');
          setIsAuthenticated(false);
          dispatch(setUser(null));
          history.push('/login');
          setLoggingOff(false);
          resolve(true);
        })
        .catch(err => {
          setLoggingOff(false);
          reject(new Error(err));
        });
    });
  }, [dispatch]);

  const checkAuth = useCallback((): boolean => {
    return !!localStorage.getItem('universo-plastica@token');
  }, []);

  return (
    <AuthContext.Provider value={{ login, logout, checkAuth, isAuthenticated, isLoading, loggingOff }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) throw new Error('This hook must be in Auth Context Component');

  return context;
}

export default AuthProvider;
